import { Button, Group, Modal, Stack, Text } from '@mantine/core';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useBeforeUnload, useBlocker } from 'react-router-dom';

type LocationLike = {
  pathname: string;
  search: string;
  hash?: string;
};

export type NavigationAttempt = {
  currentLocation: LocationLike;
  nextLocation: LocationLike;
};

export type UnsavedChangesSource = {
  enabled?: boolean;
  isDirty: boolean | (() => boolean);
  onSave?: () => unknown | Promise<unknown>;
  searchParams?: readonly string[];
  shouldBlock?: (attempt: NavigationAttempt) => boolean;
};

/** Действие, которое потеряет несохранённые правки, если его выполнить. */
export type ConfirmDiscard = (action: () => void) => void;

type SourceReader = () => UnsavedChangesSource;

type UnsavedChangesContextValue = {
  register: (id: string, readSource: SourceReader) => () => void;
  confirmDiscard: ConfirmDiscard;
};

const UnsavedChangesContext = createContext<UnsavedChangesContextValue | null>(
  null,
);

const readIsDirty = (source: UnsavedChangesSource) =>
  typeof source.isDirty === 'function' ? source.isDirty() : source.isDirty;

const isRelevantNavigation = (
  source: UnsavedChangesSource,
  attempt: NavigationAttempt,
) => {
  if (source.shouldBlock) {
    return source.shouldBlock(attempt);
  }

  const { currentLocation, nextLocation } = attempt;
  if (currentLocation.pathname !== nextLocation.pathname) {
    return true;
  }

  const currentSearch = new URLSearchParams(currentLocation.search);
  const nextSearch = new URLSearchParams(nextLocation.search);
  return (source.searchParams ?? ['selected']).some(
    (key) => currentSearch.get(key) !== nextSearch.get(key),
  );
};

export function useUnsavedChangesSource(source: UnsavedChangesSource) {
  const context = useContext(UnsavedChangesContext);
  const generatedId = useId();
  const sourceRef = useRef(source);
  sourceRef.current = source;

  useEffect(() => {
    if (!context) return;
    return context.register(generatedId, () => sourceRef.current);
  }, [context, generatedId]);
}

const runWithoutConfirmation: ConfirmDiscard = (action) => action();

/**
 * Подтверждение для действий вне навигации: роутер их не видит, а правки они теряют
 * так же, как переход (перезагрузка данных пересобирает представление). Без провайдера
 * действие выполняется сразу - вне приложения подтверждать нечем.
 */
export function useUnsavedChangesConfirm(): ConfirmDiscard {
  const context = useContext(UnsavedChangesContext);
  return context?.confirmDiscard ?? runWithoutConfirmation;
}

export function UnsavedChangesProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const sourcesRef = useRef(new Map<string, SourceReader>());
  const [opened, setOpened] = useState(false);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);

  const register = useCallback((id: string, readSource: SourceReader) => {
    sourcesRef.current.set(id, readSource);
    return () => {
      sourcesRef.current.delete(id);
    };
  }, []);

  const hasDirtySource = useCallback(
    () =>
      Array.from(sourcesRef.current.values()).some((readSource) => {
        const source = readSource();
        return source.enabled !== false && readIsDirty(source);
      }),
    [],
  );

  const confirmDiscard = useCallback<ConfirmDiscard>(
    (action) => {
      if (!hasDirtySource()) {
        action();
        return;
      }
      setPendingAction(() => action);
    },
    [hasDirtySource],
  );

  const blocker = useBlocker(
    useCallback((attempt) => {
      return Array.from(sourcesRef.current.values()).some((readSource) => {
        const source = readSource();
        return (
          source.enabled !== false &&
          readIsDirty(source) &&
          isRelevantNavigation(source, attempt)
        );
      });
    }, []),
  );

  useBeforeUnload(
    useCallback(
      (event) => {
        if (hasDirtySource()) {
          event.preventDefault();
          event.returnValue = '';
        }
      },
      [hasDirtySource],
    ),
    { capture: true },
  );

  useEffect(() => {
    if (blocker.state === 'blocked') {
      setOpened(true);
    }
  }, [blocker.state]);

  const cancelNavigation = useCallback(() => {
    setOpened(false);
    setPendingAction(null);
    if (blocker.state === 'blocked') {
      blocker.reset();
    }
  }, [blocker]);

  const leaveWithoutSaving = useCallback(() => {
    setOpened(false);
    if (pendingAction) {
      setPendingAction(null);
      pendingAction();
      return;
    }
    if (blocker.state === 'blocked') {
      blocker.proceed();
    }
  }, [blocker, pendingAction]);

  const contextValue = useMemo(
    () => ({ register, confirmDiscard }),
    [register, confirmDiscard],
  );

  // Действие подтверждают тем же окном, но уходить со страницы оно не предлагает.
  const isDiscardPending = pendingAction !== null;

  return (
    <UnsavedChangesContext.Provider value={contextValue}>
      {children}
      <Modal
        data-testid='unsaved-changes-modal'
        opened={(opened && blocker.state === 'blocked') || isDiscardPending}
        onClose={cancelNavigation}
        title={t('navigation.unsavedChanges.title')}
        closeOnClickOutside={false}
        closeOnEscape
        withCloseButton
        closeButtonProps={{
          'data-testid': 'unsaved-changes-close',
        }}
      >
        <Stack gap='md'>
          <Text>
            {t(
              isDiscardPending
                ? 'navigation.unsavedChanges.discardMessage'
                : 'navigation.unsavedChanges.message',
            )}
          </Text>
          <Group justify='flex-end' gap={'xs'}>
            <Button
              data-testid='unsaved-changes-stay'
              variant='default'
              onClick={cancelNavigation}
            >
              {t('navigation.unsavedChanges.stay')}
            </Button>
            <Button
              data-testid='unsaved-changes-leave'
              variant='filled'
              color='red'
              onClick={leaveWithoutSaving}
            >
              {t(
                isDiscardPending
                  ? 'navigation.unsavedChanges.discard'
                  : 'navigation.unsavedChanges.leave',
              )}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </UnsavedChangesContext.Provider>
  );
}

export { useBlockRedirect } from './use-block-redirect';
